<template>
  <div class="converter-panel clipr-theme twitch-theme">
    <div class="url-icons-preload" aria-hidden="true">
      <img :src="googleDriveIconUrl" alt="" />
    </div>

    <section class="form-section url-section">
      <div class="url-section-label">
        <span>{{ tt("makeClips.enterVideoUrl", "Ingresa URL del video") }}</span>
        <div class="url-section-platform-icons" aria-hidden="true">
          <img
            :src="googleDriveIconUrl"
            alt=""
            class="url-section-platform-icon"
          />
        </div>
      </div>
      <div class="url-input-unified">
        <div class="url-input-icon-segment">
          <i class="ti-link"></i>
        </div>
        <input
          v-model.trim="driveUrl"
          type="url"
          class="url-input"
          :placeholder="urlPlaceholder"
          :disabled="preparing"
        />
        <div class="url-input-badge-segment url-input-badge-segment--google_drive">
          <img
            :src="googleDriveIconUrl"
            alt=""
            class="url-badge-icon"
          />
          <span>{{ tt("schedulePost.converterFolderHandle", "Google Drive") }}</span>
        </div>
      </div>
      <div v-if="driveUrl && !isDriveUrlValid" class="input-feedback error">
        <i class="ti-alert-circle"></i>
        {{ tt("schedulePost.converterInvalidDriveUrl", "Ingresa una URL válida de Google Drive.") }}
      </div>
      <div v-if="autodetectedSourceType === 'google_drive'" class="input-feedback hint">
        <i class="ti-info-alt"></i>
        {{ tt("makeClips.googleDrivePublicAccessHint", "Para Google Drive, el archivo debe estar compartido como \"Cualquiera con el enlace\" y permitir descarga.") }}
      </div>
      <div v-if="errorMessage" class="input-feedback error">
        <i class="ti-alert-circle"></i>
        {{ errorMessage }}
      </div>
    </section>

    <section class="submit-section submit-section-inline submit-section-primary">
      <div class="submit-wrapper">
        <div class="submit-rail">
          <div class="submit-rail-copy">
            <span class="submit-rail-kicker">
              {{ tt("schedulePost.converterFolderTitle", "Converter PRO") }}
            </span>
            <h3 class="submit-rail-title">
              {{ tt("schedulePost.converterPanelTitle", "Convierte tu video completo de Drive") }}
            </h3>
            <p class="submit-rail-text">
              {{ tt("schedulePost.converterPanelSubtitle", "Pega una URL pública de Google Drive (máx. 5 minutos), elige tipo de video y confirma para procesar el video completo.") }}
            </p>
          </div>
          <div class="submit-rail-actions">
            <button
              type="button"
              class="submit-btn"
              :disabled="!canOpenPrepareConfirm"
              @click="openPrepareConfirmModal"
            >
              <i :class="preparing ? 'ti-reload rotating mr-1' : 'ti-video-camera mr-1'"></i>
              {{ preparing ? tt("makeClips.inQueue", "En cola...") : tt("schedulePost.converterStartFullVideo", "Convertir video completo") }}
              <span
                v-if="shouldShowProBadge"
                class="converter-pro-badge converter-pro-badge--inline"
                aria-hidden="true"
              >PRO</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <section class="converter-video-type-section">
      <label class="converter-video-type-label">
        {{ tt("vodClips.videoTypeLabel", "Tipo de video") }}
      </label>
      <select
        v-model="selectedVideoType"
        class="form-control converter-video-type-select"
        :disabled="preparing"
      >
        <option v-for="option in videoTypeOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
    </section>

    <div class="converter-library-toolbar">
      <p class="converter-library-note">
        {{ tt("schedulePost.converterFolderSubtitle", "Convierte videos completos de Google Drive (máx. 5 minutos) en una sola cola.") }}
      </p>
      <button
        type="button"
        class="btn btn-outline-primary btn-sm"
        :disabled="loadingJobs || preparing"
        @click="refreshJobs()"
      >
        <i :class="loadingJobs ? 'ti-reload rotating' : 'ti-reload'"></i>
        {{ tt("common.refresh", "Refresh") }}
      </button>
    </div>

    <clipr-loader
      v-if="loadingJobs"
      variant="inline"
      :title="tt('common.loading', 'Cargando...')"
    />
    <div v-else-if="jobs.length === 0" class="converter-empty-state">
      <i class="ti-video-camera"></i>
      <p>{{ tt("schedulePost.converterNoHistory", "Aún no tienes conversiones en este folder.") }}</p>
    </div>
    <div v-else class="converter-jobs-strip">
      <div
        v-for="job in jobs"
        :key="job.id"
        class="video-card"
        :class="{ 'video-card-rendering': isConverterJobProcessing(job) }"
      >
        <div class="video-card-header">
          <div class="video-card-status">
            <span class="badge" :class="getConverterJobStatusBadgeClass(job)">
              {{ getJobStatusLabel(job) }}
            </span>
          </div>
        </div>
        <div
          class="video-card-media"
          :class="{
            'video-card-media--rendering': isConverterJobProcessing(job),
            'video-card-media--rendered': isConverterJobRendered(job),
          }"
        >
          <div
            class="video-card-thumbnail"
            :class="{
              'video-card-thumbnail-rendering': isConverterJobProcessing(job),
              'video-card-thumbnail-rendered': isConverterJobRendered(job),
            }"
          >
            <div v-if="getJobThumbnailUrl(job)" class="video-card-image">
              <img
                :src="getJobThumbnailUrl(job)"
                :alt="`${tt('vodClips.thumbnailAltPrefix', 'Thumbnail')} ${job.id}`"
                loading="lazy"
                decoding="async"
              />
              <button
                type="button"
                class="video-card-play-btn"
                :disabled="!getJobPlaybackUrl(job) || isConverterJobInteractionLocked(job)"
                @click.stop
              >
                <i class="ti-control-play"></i>
              </button>
            </div>
            <div v-else class="video-card-placeholder">
              <i class="ti-video-camera"></i>
              <button
                type="button"
                class="video-card-play-btn"
                :disabled="!getJobPlaybackUrl(job) || isConverterJobInteractionLocked(job)"
                @click.stop
              >
                <i class="ti-control-play"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="video-card-meta">
          <div class="video-card-title-row">
            <div class="video-card-title" :title="getJobTitle(job)">
              {{ getJobTitle(job) }}
            </div>
            <div
              class="video-card-duration"
              :class="{ 'video-card-duration--empty': getJobDurationSeconds(job) <= 0 }"
            >
              {{ getJobDurationSeconds(job) > 0 ? formatDuration(getJobDurationSeconds(job)) : "\u00A0" }}
            </div>
          </div>
          <div class="video-card-actions video-card-actions--minimal">
            <div class="video-card-actions-primary">
              <button
                type="button"
                class="video-card-icon-btn video-card-icon-btn-fullscreen"
                :title="tt('vodClips.fullscreen', 'Pantalla completa')"
                :disabled="!getJobPlaybackUrl(job) || isConverterJobInteractionLocked(job)"
                @click.stop
              >
                <i class="ti-fullscreen"></i>
              </button>
              <button
                type="button"
                class="video-card-icon-btn video-card-icon-btn-mute"
                :title="tt('vodClips.mute', 'Silenciar')"
                :disabled="!getJobPlaybackUrl(job) || isConverterJobInteractionLocked(job)"
                @click.stop
              >
                <span class="video-card-mute-icon" aria-hidden="true">
                  <svg
                    class="video-card-mute-icon-svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 9.5v5h3.5l4.5 3.6V5.9L7.5 9.5H4z"
                      fill="currentColor"
                    />
                    <path
                      d="M14.8 8.2a5.2 5.2 0 010 7.6M17.2 6.1a8.4 8.4 0 010 11.8"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />
                    <circle cx="18.5" cy="18.5" r="4.8" fill="#ef4444" stroke="#0f172a" stroke-width="1.1" />
                    <path
                      d="M16.5 16.5l4 4M20.5 16.5l-4 4"
                      stroke="#fff"
                      stroke-width="1.7"
                      stroke-linecap="round"
                    />
                  </svg>
                </span>
              </button>
              <button
                type="button"
                class="video-card-icon-btn video-card-icon-btn-download"
                :title="tt('publicResults.download', 'Descargar')"
                :disabled="!getJobPlaybackUrl(job) || isConverterJobInteractionLocked(job)"
                @click.stop
              >
                <i class="ti-download"></i>
              </button>
              <button
                type="button"
                class="video-card-icon-btn video-card-icon-btn-delete"
                :title="tt('common.delete', 'Eliminar')"
                :disabled="isConverterJobInteractionLocked(job)"
                @click.stop
              >
                <i class="ti-trash"></i>
              </button>
            </div>
            <div class="video-card-actions-premium video-card-actions-premium--minimal">
              <button
                type="button"
                class="video-card-icon-btn video-card-icon-btn-schedule"
                :title="tt('schedulePost.schedule', 'Programar')"
                :disabled="isConverterJobInteractionLocked(job)"
                @click.stop
              >
                <i class="ti-calendar"></i>
                <span
                  v-if="shouldShowProBadge"
                  class="video-card-pro-badge"
                  aria-hidden="true"
                >PRO</span>
              </button>
              <button
                type="button"
                class="video-card-icon-btn video-card-icon-btn-editor video-card-icon-btn-editor-pro"
                :title="tt('vodClips.editor', 'Editor')"
                :disabled="isConverterJobInteractionLocked(job)"
                @click.stop
              >
                <i class="ti-pencil"></i>
                <span
                  v-if="shouldShowProBadge"
                  class="video-card-pro-badge"
                  aria-hidden="true"
                >PRO</span>
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="isConverterJobProcessing(job)"
          class="video-card-rendering-overlay"
          role="status"
          aria-live="polite"
        >
          <span class="processing-spinner processing-spinner-rendering" aria-hidden="true"></span>
          <span class="video-card-rendering-text">
            {{ getConverterJobRenderingOverlayLabel(job) }}
            <span class="processing-dots" aria-hidden="true">
              <span>.</span><span>.</span><span>.</span>
            </span>
          </span>
        </div>
      </div>
    </div>

    <transition name="modal-fade">
      <body-portal v-if="showPrepareConfirmModal">
        <div class="confirm-modal-overlay" @click.self="closePrepareConfirmModal">
          <div
            class="confirm-modal confirm-modal--create converter-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="converter-prepare-modal-title"
          >
            <div class="confirm-modal-header confirm-modal-header--centered">
              <div class="confirm-modal-header-icon">
                <i class="ti-video-camera"></i>
              </div>
              <h3 id="converter-prepare-modal-title" class="confirm-modal-title">
                {{ tt("schedulePost.converterConfirmTitle", "Confirmar conversión de video completo") }}
              </h3>
              <p class="confirm-modal-subtitle">
                {{ tt("schedulePost.converterPrepareConfirmSubtitle", "Verifica el resumen antes de encolar la conversión del video completo de Google Drive.") }}
              </p>
              <button
                type="button"
                class="confirm-modal-close"
                :disabled="preparing"
                @click="closePrepareConfirmModal"
              >
                <i class="ti-close"></i>
              </button>
            </div>

            <div class="confirm-modal-body">
              <div class="confirm-modal-content">
                <div class="confirm-video-card">
                  <div class="confirm-video-thumb-wrap">
                    <div class="confirm-video-thumb-placeholder">
                      <i class="ti-video-camera"></i>
                    </div>
                    <div class="confirm-video-duration-badge">
                      {{ formatDuration(preparePreviewDurationSeconds) }}
                    </div>
                  </div>
                  <div class="confirm-video-info">
                    <h4 class="confirm-video-title">{{ trimmedDriveUrl }}</h4>
                    <div class="confirm-video-meta">
                      <span class="confirm-video-meta-item">
                        <i class="ti-rss-alt"></i>
                        {{ tt("schedulePost.converterFolderHandle", "Google Drive") }}
                      </span>
                      <span class="confirm-video-meta-item">
                        <i class="ti-timer"></i>
                        {{ tt("schedulePost.converterDurationMax", "Máximo 5 minutos") }}
                      </span>
                      <span class="confirm-video-meta-item">
                        <i class="ti-layout"></i>
                        {{ selectedVideoTypeLabel }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="confirm-token-card">
                  <div class="confirm-token-header">
                    <i class="fa-solid fa-coins" aria-hidden="true"></i>
                    <div class="confirm-token-header-copy">
                      <span class="confirm-token-header-title">{{ tt("makeClips.tokensSummary", "Resumen de tokens") }}</span>
                      <small class="confirm-token-header-rate">{{ tt("makeClips.tokenMinuteRate", "1 token = 1 minuto de render") }}</small>
                    </div>
                  </div>
                  <div class="confirm-token-body">
                    <div class="confirm-token-row">
                      <span>{{ tt("makeClips.processingTime", "Tiempo a procesar") }}</span>
                      <span class="confirm-token-balance">{{ formatDuration(preparePreviewDurationSeconds) }}</span>
                    </div>
                    <div class="confirm-token-row">
                      <span>{{ tt("makeClips.estimatedCost", "Costo estimado") }}</span>
                      <span class="confirm-token-cost">
                        {{ prepareEstimatedTokenCost }} {{ prepareEstimatedTokenCost === 1 ? tt("makeClips.tokenSingular", "token") : tt("makeClips.tokenPlural", "tokens") }}
                      </span>
                    </div>
                    <div class="confirm-token-hint">
                      {{ tt("schedulePost.converterTokenChargeHint", "El cobro final usa la duración real detectada por el worker al procesar el video completo.") }}
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="prepareModalErrorMessage" class="confirm-modal-state confirm-modal-state-error">
                <i class="ti-alert-circle"></i>
                <span>{{ prepareModalErrorMessage }}</span>
              </div>
            </div>

            <div class="confirm-modal-footer">
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--secondary"
                :disabled="preparing"
                @click="closePrepareConfirmModal"
              >
                {{ tt("usersList.cancel", "Cancelar") }}
              </button>
              <button
                type="button"
                class="confirm-modal-btn confirm-modal-btn--primary"
                :disabled="preparing || !isDriveUrlValid"
                @click="confirmPrepareJob"
              >
                <i :class="preparing ? 'ti-reload rotating' : 'ti-check'"></i>
                {{ preparing ? tt("makeClips.inQueue", "En cola...") : tt("schedulePost.converterConfirmAndQueue", "Confirmar y convertir video") }}
              </button>
            </div>
          </div>
        </div>
      </body-portal>
    </transition>
  </div>
</template>

<script>
import { supabase } from "@/supabase";
import { fetchConverterJobs, prepareConverterJob } from "@/api/converter";
import CliprLoader from "@/components/clipr/CliprLoader.vue";

const GOOGLE_DRIVE_ICON_URL = "https://cdn.simpleicons.org/googledrive/34A853";

const GOOGLE_DRIVE_URL_PLACEHOLDER = "https://drive.google.com/file/d/FILE_ID/view";

const VIDEO_TYPES = Object.freeze([
  { value: "auto", key: "vodClips.videoTypeAuto", fallback: "Auto" },
  { value: "gameplay", key: "vodClips.videoTypeGameplay", fallback: "Gameplay" },
  { value: "just_chatting", key: "vodClips.videoTypeJustChatting", fallback: "Just Chatting" },
  { value: "irl", key: "vodClips.videoTypeIRL", fallback: "IRL" },
  { value: "podcast", key: "vodClips.videoTypePodcast", fallback: "Podcast" },
  { value: "center", key: "vodClips.videoTypeCenter", fallback: "Vertical centrado" },
]);

export default {
  name: "ConverterCollectionPanel",
  components: {
    CliprLoader,
  },
  props: {
    translate: {
      type: Function,
      default: null,
    },
    hasActivePlan: {
      type: Boolean,
      default: false,
    },
    isAdminUser: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      driveUrl: "",
      preparing: false,
      loadingJobs: false,
      jobs: [],
      errorMessage: "",
      prepareModalErrorMessage: "",
      showPrepareConfirmModal: false,
      selectedVideoType: "auto",
      pollTimer: null,
      googleDriveIconUrl: GOOGLE_DRIVE_ICON_URL,
    };
  },
  computed: {
    trimmedDriveUrl() {
      return String(this.driveUrl || "").trim();
    },
    autodetectedSourceType() {
      return this.detectSourceType(this.trimmedDriveUrl);
    },
    urlPlaceholder() {
      return GOOGLE_DRIVE_URL_PLACEHOLDER;
    },
    isDriveUrlValid() {
      if (!this.trimmedDriveUrl) return false;
      return this.detectSourceType(this.trimmedDriveUrl) === "google_drive" && this.validateGoogleDriveUrl(this.trimmedDriveUrl);
    },
    canOpenPrepareConfirm() {
      if (this.preparing || this.loadingJobs) return false;
      return this.isDriveUrlValid;
    },
    canUseProActions() {
      return this.hasActivePlan || this.isAdminUser;
    },
    shouldShowProBadge() {
      return !this.canUseProActions;
    },
    preparePreviewDurationSeconds() {
      return 300;
    },
    prepareEstimatedTokenCost() {
      return Math.ceil(this.preparePreviewDurationSeconds / 60);
    },
    videoTypeOptions() {
      return VIDEO_TYPES.map((option) => ({
        value: option.value,
        label: this.tt(option.key, option.fallback),
      }));
    },
    selectedVideoTypeLabel() {
      const selectedValue = String(this.selectedVideoType || "auto").trim().toLowerCase();
      const option = this.videoTypeOptions.find((item) => item.value === selectedValue);
      return option?.label || this.tt("vodClips.videoTypeAuto", "Auto");
    },
  },
  async mounted() {
    await this.refreshJobs();
    this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    requestProAction() {
      if (this.canUseProActions) return true;
      this.$emit("pro-action-blocked");
      return false;
    },
    tt(key, fallback) {
      if (typeof this.translate === "function") return this.translate(key, fallback);
      return fallback;
    },
    detectSourceType(value) {
      const raw = String(value || "").trim();
      if (!raw) return null;
      if (/^https?:\/\/(www\.)?twitch\.tv\//.test(raw)) return "twitch";
      if (/^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//.test(raw)) return "youtube";
      if (/^https?:\/\/(www\.)?facebook\.com\//.test(raw)) return "facebook";
      if (/^https?:\/\/(www\.)?kick\.com\//.test(raw)) return "kicks";
      if (/^https?:\/\/(drive|docs)\.google\.com\//.test(raw)) return "google_drive";
      return null;
    },
    validateGoogleDriveUrl(value) {
      try {
        const parsed = new URL(value);
        const hostname = String(parsed.hostname || "").toLowerCase();
        if (hostname !== "drive.google.com" && hostname !== "docs.google.com") return false;
        return (
          /\/file\/d\/[^/]+/.test(parsed.pathname)
          || parsed.searchParams.has("id")
          || parsed.pathname.includes("/uc")
        );
      } catch (_error) {
        return false;
      }
    },
    normalizeVideoType(value) {
      const normalized = String(value || "auto").trim().toLowerCase();
      return VIDEO_TYPES.some((option) => option.value === normalized) ? normalized : "auto";
    },
    formatDuration(secondsValue) {
      const total = Math.max(0, Math.round(Number(secondsValue || 0)));
      const minutes = Math.floor(total / 60);
      const seconds = String(total % 60).padStart(2, "0");
      return `${minutes}:${seconds}`;
    },
    getJobRenderedClip(job) {
      return job?.rendered_clip && typeof job.rendered_clip === "object"
        ? job.rendered_clip
        : null;
    },
    getJobThumbnailUrl(job) {
      const renderedClip = this.getJobRenderedClip(job);
      return String(renderedClip?.thumbnail_url || "").trim();
    },
    getJobDurationSeconds(job) {
      const renderedClip = this.getJobRenderedClip(job);
      const start = Number(renderedClip?.start_seconds ?? 0);
      const end = Number(renderedClip?.end_seconds ?? 0);
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
      return end - start;
    },
    getJobTitle(job) {
      const sourceUrl = String(job?.source_url || "").trim();
      if (sourceUrl) return sourceUrl;
      return this.tt("schedulePost.converterEditorNoTitle", "Converter job");
    },
    getJobPlaybackUrl(job) {
      const renderedClip = this.getJobRenderedClip(job);
      return String(renderedClip?.rendered_video_url || "").trim();
    },
    getConverterJobStatusBadgeClass(job) {
      const status = String(job?.status || "").trim().toLowerCase();
      if (status === "6_rendered") return "badge-status-posted";
      if (status === "2.1_failed") return "badge-status-post-failed";
      if (status === "4_rendering" || status === "5_rendering") return "badge-status-rendering";
      return "badge-status-default";
    },
    getConverterJobRenderingOverlayLabel(job) {
      const status = String(job?.status || "").trim().toLowerCase();
      if (status === "4_rendering" || status === "5_rendering") {
        return this.tt("schedulePost.converterStatusRendering", "Renderizando");
      }
      if (status === "2_downloading") {
        return this.tt("schedulePost.converterStatusDownloading", "Descargando");
      }
      return this.tt("schedulePost.converterStatusProcessing", "Procesando");
    },
    getJobStatusLabel(job) {
      const status = String(job?.status || "").trim().toLowerCase();
      if (status === "6_rendered") return this.tt("schedulePost.converterStatusRendered", "Renderizado");
      if (status === "2.1_failed") return this.tt("schedulePost.converterStatusFailed", "Fallido");
      if (status === "1_queued") return this.tt("schedulePost.converterStatusQueued", "En cola");
      if (status === "2_downloading") return this.tt("schedulePost.converterStatusDownloading", "Descargando");
      if (status === "3_downloaded") return this.tt("schedulePost.converterStatusProcessing", "Procesando");
      if (status === "4_rendering" || status === "5_rendering") {
        return this.tt("schedulePost.converterStatusRendering", "Renderizando");
      }
      return this.tt("schedulePost.converterStatusProcessing", "Procesando");
    },
    isConverterJobInteractionLocked(job) {
      return this.isConverterJobProcessing(job) || this.isConverterJobFailed(job);
    },
    isConverterJobRendered(job) {
      return String(job?.status || "").trim().toLowerCase() === "6_rendered";
    },
    isConverterJobFailed(job) {
      return String(job?.status || "").trim().toLowerCase() === "2.1_failed";
    },
    isConverterJobProcessing(job) {
      const status = String(job?.status || "").trim().toLowerCase();
      return status !== "6_rendered" && status !== "2.1_failed";
    },
    openPrepareConfirmModal() {
      if (!this.canOpenPrepareConfirm) return;
      if (!this.requestProAction()) return;
      this.prepareModalErrorMessage = "";
      this.showPrepareConfirmModal = true;
    },
    closePrepareConfirmModal() {
      if (this.preparing) return;
      this.showPrepareConfirmModal = false;
      this.prepareModalErrorMessage = "";
    },
    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        this.refreshJobs({ silent: true });
      }, 15000);
    },
    stopPolling() {
      if (!this.pollTimer) return;
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    emitStats() {
      const totalJobs = this.jobs.length;
      const renderedJobs = this.jobs.filter((job) => String(job?.status || "").toLowerCase() === "6_rendered").length;
      const preparedJobs = this.jobs.filter((job) => this.isConverterJobProcessing(job)).length;
      this.$emit("stats-change", { totalJobs, renderedJobs, preparedJobs });
    },
    async refreshJobs({ silent = false } = {}) {
      if (!silent) {
        this.loadingJobs = true;
      }
      this.errorMessage = "";
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          this.jobs = [];
          this.emitStats();
          return;
        }
        const response = await fetchConverterJobs(session);
        const jobs = Array.isArray(response?.jobs) ? response.jobs : [];
        this.jobs = jobs;
        this.emitStats();
      } catch (error) {
        this.errorMessage = String(
          error?.message || this.tt("schedulePost.converterLoadError", "No se pudo cargar el historial de converter.")
        );
      } finally {
        this.loadingJobs = false;
      }
    },
    async confirmPrepareJob() {
      if (!this.canOpenPrepareConfirm || this.preparing) return;
      if (!this.requestProAction()) return;
      this.preparing = true;
      this.errorMessage = "";
      this.prepareModalErrorMessage = "";
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error(this.tt("schedulePost.errorLoginToSchedule", "Debes iniciar sesión para programar un clip."));
        }
        await prepareConverterJob(session, {
          source_url: this.trimmedDriveUrl,
          source_type: "google_drive",
          video_type: this.normalizeVideoType(this.selectedVideoType),
        });
        this.showPrepareConfirmModal = false;
        this.driveUrl = "";
        this.selectedVideoType = "auto";
        await this.refreshJobs();
      } catch (error) {
        const message = String(
          error?.message || this.tt("schedulePost.converterPrepareError", "No se pudo encolar la conversión del video.")
        );
        this.errorMessage = message;
        this.prepareModalErrorMessage = message;
      } finally {
        this.preparing = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/sass/clipr/confirm-modal";
@import "@/assets/sass/clipr/video-card";

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-active .confirm-modal,
.modal-fade-leave-active .confirm-modal {
  transition: transform 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .confirm-modal,
.modal-fade-leave-to .confirm-modal {
  transform: scale(0.96) translateY(-8px);
}

.converter-panel {
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid rgba(139, 92, 246, 0.34);
  background: linear-gradient(165deg, rgba(18, 13, 31, 0.88) 0%, rgba(15, 18, 28, 0.76) 100%);
  box-shadow: 0 14px 32px rgba(2, 6, 23, 0.35);
}

.url-icons-preload {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.url-section {
  margin-bottom: 20px;
}

.converter-video-type-section {
  margin-bottom: 18px;
}

.converter-video-type-label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--clipr-text-secondary);
}

.converter-video-type-select {
  max-width: 280px;
}

.url-section-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 16px;
  font-weight: 600;
  color: var(--clipr-text-primary);
  margin-bottom: 14px;
}

.url-section-platform-icons {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.url-section-platform-icon {
  width: 14px;
  height: 14px;
  object-fit: contain;
  display: block;
}

.url-input-unified {
  display: flex;
  align-items: stretch;
  background: var(--clipr-bg-elevated);
  border: 2px solid var(--clipr-border-purple);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.25s ease;
  box-shadow: 0 0 0 0 var(--clipr-purple-glow);
}

.url-input-unified:focus-within {
  border-color: var(--clipr-purple-light);
  box-shadow: 0 0 20px var(--clipr-purple-glow);
}

.url-input-icon-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  background: var(--clipr-purple-overlay-soft);
}

.url-input-icon-segment i {
  font-size: 18px;
  color: var(--clipr-purple-light);
}

.url-input-unified .url-input {
  flex: 1;
  min-width: 0;
  padding: 16px 18px;
  font-size: 12px;
  background: transparent;
  border: none;
  color: var(--clipr-text-primary);
}

.url-input-unified .url-input:focus {
  outline: none;
}

.url-input-unified .url-input::placeholder {
  color: var(--clipr-text-tertiary);
}

.url-input-unified .url-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.url-input-badge-segment {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 20px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.url-input-badge-segment i {
  font-size: 16px;
  color: inherit;
}

.url-badge-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex-shrink: 0;
}

.url-input-badge-segment--pending {
  color: var(--clipr-text-tertiary);
}

.url-input-badge-segment--twitch {
  color: #a78bfa;
}

.url-input-badge-segment--youtube {
  color: #f87171;
}

.url-input-badge-segment--facebook {
  color: #60a5fa;
}

.url-input-badge-segment--kicks {
  color: #4ade80;
}

.url-input-badge-segment--google_drive {
  color: #86efac;
}

.input-feedback {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 13px;
}

.input-feedback.error {
  color: #f87171;
}

.input-feedback.hint {
  color: var(--clipr-text-tertiary);
}

.input-feedback i {
  font-size: 14px;
  flex-shrink: 0;
}

.submit-section {
  margin: 0 0 18px 0;
  padding-top: 0;
}

.submit-wrapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
}

.submit-rail {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 18px;
  border-radius: 18px;
  border: 1px solid rgba(168, 85, 247, 0.2);
  background:
    radial-gradient(circle at top left, rgba(168, 85, 247, 0.18), transparent 42%),
    linear-gradient(135deg, rgba(15, 18, 32, 0.96) 0%, rgba(9, 12, 22, 0.92) 100%);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.24);
}

.submit-rail-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.submit-rail-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--clipr-purple-light);
}

.submit-rail-title {
  margin: 0;
  font-size: 22px;
  line-height: 1.1;
  color: var(--clipr-text-primary);
}

.submit-rail-text {
  margin: 0;
  max-width: 56ch;
  font-size: 13px;
  line-height: 1.55;
  color: var(--clipr-text-secondary);
}

.submit-rail-actions {
  width: min(100%, 260px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
}

.submit-btn {
  width: 100%;
  min-width: 0;
  padding: 14px 20px;
  font-size: 15px;
  font-weight: 700;
  background: var(--clipr-purple-gradient) !important;
  box-shadow: var(--clipr-shadow-button), 0 0 20px var(--clipr-purple-glow) !important;
  border: none !important;
  border-radius: 12px !important;
  color: #fff;
  transition: all 0.3s ease !important;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--clipr-shadow-button-hover), 0 0 28px var(--clipr-purple-glow) !important;
}

.submit-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

.converter-pro-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 999px;
  border: 1px solid rgba(236, 72, 153, 0.45);
  background: rgba(236, 72, 153, 0.14);
  color: #f9a8d4;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  line-height: 1;
}

.converter-pro-badge--inline {
  margin-left: 10px;
}

.converter-library-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
}

.converter-library-note {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--clipr-text-secondary);
}

.converter-loading-inline {
  margin-top: 10px;
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--clipr-text-tertiary);
  font-size: 12px;
}

.converter-empty-state {
  margin-top: 14px;
  padding: 28px 16px;
  border-radius: 16px;
  border: 1px dashed rgba(148, 163, 184, 0.28);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: var(--clipr-text-tertiary);
  text-align: center;
}

.converter-empty-state i {
  font-size: 26px;
  color: var(--clipr-purple-light);
}

.converter-empty-state p {
  margin: 0;
  font-size: 13px;
}

.confirm-modal-content {
  display: grid;
  gap: 12px;
}

.confirm-video-card {
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(15, 23, 42, 0.45);
}

.confirm-video-thumb-wrap {
  position: relative;
  width: 96px;
  flex-shrink: 0;
}

.confirm-video-thumb-placeholder {
  width: 100%;
  height: 68px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.26), rgba(59, 130, 246, 0.2));
  color: #c4b5fd;
  font-size: 22px;
}

.confirm-video-duration-badge {
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(2, 6, 23, 0.85);
  border: 1px solid rgba(148, 163, 184, 0.35);
  color: #f8fafc;
  font-size: 11px;
  font-weight: 700;
}

.confirm-video-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.confirm-video-title {
  margin: 0;
  font-size: 0.95rem;
  color: var(--clipr-text-primary);
  line-height: 1.4;
  word-break: break-word;
}

.confirm-video-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
}

.confirm-video-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--clipr-text-secondary);
  font-size: 0.8rem;
}

.confirm-token-card {
  padding: 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(16, 185, 129, 0.06) 100%);
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.confirm-token-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
}

.confirm-token-header i {
  color: #22c55e;
  font-size: 1rem;
  margin-top: 2px;
}

.confirm-token-header-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.confirm-token-header-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--clipr-text-primary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.confirm-token-header-rate {
  font-size: 0.73rem;
  font-weight: 500;
  color: var(--clipr-text-secondary);
}

.confirm-token-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.confirm-token-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  color: var(--clipr-text-secondary);
  padding: 2px 0;
}

.confirm-token-cost,
.confirm-token-balance {
  font-weight: 600;
  color: var(--clipr-text-primary);
}

.confirm-token-hint {
  margin-top: 0;
  font-size: 0.78rem;
  color: var(--clipr-text-secondary);
  line-height: 1.45;
}

@media (max-width: 1024px) {
  .submit-rail {
    flex-direction: column;
    align-items: stretch;
  }

  .submit-rail-actions {
    width: 100%;
  }
}

@media (max-width: 900px) {
  .url-input-badge-segment {
    flex: 0 0 auto;
    min-width: 0;
    padding-left: 12px;
    padding-right: 12px;
    justify-content: center;
  }

  .url-input-badge-segment span {
    display: none;
  }

  .converter-library-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
